import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import UITour from 'ui-shepherd-react/tour/UITour';
import MultiTourHandler from 'ui-shepherd-react/lib/MultiTourHandler';
import OnboardingTourSteps from './OnboardingTourSteps';
import { OnboardingTourTrackingProvider } from './OnboardingTourTrackingProvider';
import { getTourById, getTourFromConfig } from '../util/getTour';
import { buildTourConfig } from '../util/tourConfig';
import { getNextTourFromStorage } from '../util/tourStorage';
import { getTourIdFromQueryParams, getQueryParam, parseReturnUrl } from '../util/urlUtils';
import { transformTour } from '../util/tourTransformer';
import { getStorageItem, setStorageItem } from '../util/storageUtil';
import { Metrics } from '../util/metrics';
import SentryManager from '../manager/SentryManager';
import ObscureNav from './ObscureNav';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SELENIUM_DISABLE_ONBOARDING_TOURS_KEY = 'selenium.disable.onboarding_tours';
const shouldDisableOnboardingTour = () => {
  return (
    // TODO: add link to selenium doc
    // Use the convention selenium.disable.<feature>.
    // selenium.disable.onboarding_tours = 'true' should prevent the tour from loading.
    getStorageItem(SELENIUM_DISABLE_ONBOARDING_TOURS_KEY) === 'true'
  );
};
const SELENIUM_ONBOARDING_TOUR_FLAG = 'selenium_onboarding_tour_enabled';
const SELENIUM_ONBOARDING_TOUR_CONFIG = 'selenium_onboarding_tour_config';
const setOnboardingTourConfigForSelenium = tourData => {
  if (getStorageItem(SELENIUM_ONBOARDING_TOUR_FLAG) === 'true') {
    setStorageItem(SELENIUM_ONBOARDING_TOUR_CONFIG, JSON.stringify(tourData));
  }
};
const defaultMultiTourHandler = new MultiTourHandler();
function OnboardingTour(props) {
  const {
    tourId: passedTourId,
    tour: localTour,
    multiTourHandler = defaultMultiTourHandler,
    navigationCallback,
    onTourMount
  } = props;
  const [activeTourId, setActiveTourId] = useState(null);
  const [tourState, setTourState] = useState({
    tourData: null,
    tourConfig: null
  });
  useEffect(() => {
    if (shouldDisableOnboardingTour()) {
      return;
    }
    let tourId = passedTourId || getTourIdFromQueryParams();
    let prevTourLength = 0;
    let linkedToursTotalStepCountFromStorage;
    let returnUrl = parseReturnUrl(getQueryParam('returnUrl'));
    if (!tourId) {
      // Tours from storage are set through the nextTourAlias tour config property.
      const tourDataFromStorage = getNextTourFromStorage();
      tourId = tourDataFromStorage.tourId;
      prevTourLength = tourDataFromStorage.prevTourLength || 0;
      linkedToursTotalStepCountFromStorage = tourDataFromStorage.linkedToursTotalStepCount;
      returnUrl = tourDataFromStorage.returnUrl;
    }
    if (!tourId) {
      return;
    }
    let isLocalTour = false;
    const tourConfigPromise = localTour ? getTourFromConfig(localTour).then(tourData => {
      isLocalTour = true;
      return tourData;
    }) : getTourById(tourId);
    tourConfigPromise.then(tourData_ => {
      const tourData = tourData_;
      const id = tourId || tourData.alias;

      // If the tourId does not match the local tour alias, abort
      if (isLocalTour && tourId !== tourData.alias) {
        console.warn(`Skipping this tour because the provided tourId "${tourId}" doesn't match the local tour alias "${tourData.alias}".`);
        return;
      }
      setActiveTourId(id);
      tourData.steps = transformTour(tourData.steps);
      tourData.prevTourLength = prevTourLength;
      tourData.linkedToursTotalStepCount = tourData.linkedToursTotalStepCount || linkedToursTotalStepCountFromStorage;
      tourData.returnUrl = returnUrl;
      const newTourConfig = buildTourConfig(id, tourData);
      setTourState({
        tourConfig: newTourConfig,
        tourData
      });
      setOnboardingTourConfigForSelenium(tourData);
      if (onTourMount) {
        onTourMount();
      }
    }).catch(error => {
      console.error(error);
      SentryManager.reportLoadTourError(error, tourId);
      Metrics.counter('load-onboarding-tour-error', {
        tourId: tourId
      }).increment();
    });
  }, [passedTourId, localTour, onTourMount]);
  const {
    tourConfig,
    tourData
  } = tourState;
  const maybeObscureNavConfig = tourData && tourData.obscureNav || null;
  if (!tourData || !tourConfig || !activeTourId) {
    return null;
  }
  return /*#__PURE__*/_jsx(OnboardingTourTrackingProvider, {
    children: /*#__PURE__*/_jsxs(UITour
    // @ts-expect-error _rootNode is not typed
    // eslint-disable-next-line react/prop-types
    , {
      _rootNode: props._rootNode,
      className: "onboarding-tour-container",
      config: tourConfig,
      multiTourHandler: multiTourHandler,
      navigationCallback: navigationCallback,
      tour: activeTourId,
      children: [/*#__PURE__*/_jsx(OnboardingTourSteps, {
        tourData: tourData,
        tourId: activeTourId
      }), maybeObscureNavConfig && maybeObscureNavConfig.enabled && /*#__PURE__*/_jsx(ObscureNav, {
        exclusionSelectors: maybeObscureNavConfig.exclusionSelectors
      })]
    })
  });
}
OnboardingTour.propTypes = {
  tourId: PropTypes.string
};
export default /*#__PURE__*/memo(OnboardingTour);
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;